import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher";
import Brand from "./Brand";
import { appBarSx, pillItemSx, pillTrackSx, toolbarSx } from "./styles";
import { useAuth } from "src/providers/AuthProvider";
import { ADMIN_PATH, DASHBOARD_PATH } from "src/constants/paths";
import { AuthUser } from "src/Types/auth.types";
import { useLocale } from "src/i18n/LocaleProvider";
import { homePath } from "src/i18n/paths";

const getInitials = (user: AuthUser | null) => {
  const source = user?.name?.trim() || user?.email || "";
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return source.slice(0, 1).toUpperCase() || "?";
};

const AccountMenu: React.FC = () => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const { user, isAdmin, logout } = useAuth();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const open = Boolean(anchor);

  const handleLogout = () => {
    setAnchor(null);
    logout();
    navigate(homePath(locale));
  };

  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t("nav.accountMenu")}
        aria-haspopup="menu"
        aria-expanded={open}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              display: { xs: "none", sm: "block" },
              transition: "transform 0.2s ease",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        }
        sx={{
          minWidth: 0,
          p: 0.5,
          pr: { xs: 0.5, sm: 1.25 },
          borderRadius: 999,
          color: "#cbd5e1",
          border: "1px solid rgba(30, 41, 59, 1)",
          bgcolor: "rgba(15, 23, 42, 0.8)",
          "&:hover": { bgcolor: "rgba(30, 41, 59, 0.6)", color: "#fff" },
          "& .MuiButton-endIcon": { ml: 0.5 },
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: "0.85rem",
            fontWeight: 700,
            background: "linear-gradient(135deg, #7c3aed, #6366f1)",
            color: "#fff",
          }}
        >
          {getInitials(user)}
        </Avatar>
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 260,
            bgcolor: "#0f172a",
            border: "1px solid rgba(30, 41, 59, 1)",
            color: "#e2e8f0",
            borderRadius: "14px",
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", mb: 0.5 }}>
            {t("nav.signedInAs")}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 600, color: "#fff" }} noWrap>
              {user?.name || user?.email}
            </Typography>
            {isAdmin && (
              <Chip
                label={t("nav.roleAdmin")}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor: "rgba(124, 58, 237, 0.2)",
                  color: "#c4b5fd",
                  border: "1px solid rgba(167, 139, 250, 0.4)",
                }}
              />
            )}
          </Stack>
          {user?.name && (
            <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }} noWrap>
              {user.email}
            </Typography>
          )}
          {user?.organization.name && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ mt: 1, color: "#cbd5e1", display: { xs: "flex", md: "none" } }}
            >
              <ApartmentIcon sx={{ fontSize: 16, color: "#a78bfa" }} aria-hidden />
              <Typography sx={{ fontSize: "0.8rem" }} noWrap>
                {user.organization.name}
              </Typography>
            </Stack>
          )}
        </Box>
        <Divider sx={{ borderColor: "rgba(30, 41, 59, 1)" }} />
        <MenuItem
          onClick={handleLogout}
          sx={{
            mx: 1,
            mt: 1,
            borderRadius: "10px",
            color: "#cbd5e1",
            "&:hover": { bgcolor: "rgba(30, 41, 59, 0.6)", color: "#fff" },
          }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t("nav.logout")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default function AppNavbar() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

  const tabs = [
    {
      label: t("nav.dashboard"),
      path: DASHBOARD_PATH,
      icon: <DashboardOutlinedIcon fontSize="small" />,
    },
    ...(isAdmin
      ? [
          {
            label: t("nav.admin"),
            path: ADMIN_PATH,
            icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
          },
        ]
      : []),
  ];

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <AppBar position="sticky" elevation={0} sx={appBarSx}>
      <Toolbar sx={toolbarSx}>
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 3 }}>
          <Brand to={DASHBOARD_PATH} hideWordmarkOnMobile />

          <Box component="nav" aria-label={t("nav.workspace")} sx={pillTrackSx}>
            {tabs.map((tab) => {
              const active = isActive(tab.path);
              return (
                <Tooltip key={tab.path} title={isCompact ? tab.label : ""}>
                  <Button
                    component={Link}
                    to={tab.path}
                    aria-label={tab.label}
                    aria-current={active ? "page" : undefined}
                    startIcon={tab.icon}
                    sx={{
                      ...pillItemSx(active),
                      minWidth: 0,
                      px: { xs: 1.25, sm: 2 },
                      "& .MuiButton-startIcon": {
                        color: active ? "#fff" : "#a78bfa",
                        mr: { xs: 0, sm: 1 },
                        ml: { xs: 0, sm: -0.5 },
                      },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      {tab.label}
                    </Box>
                  </Button>
                </Tooltip>
              );
            })}
          </Box>
        </Stack>

        <Stack direction="row" spacing={{ xs: 0.5, sm: 1.5 }} alignItems="center">
          <LanguageSwitcher compact />
          {user?.organization.name && (
            <Chip
              icon={<ApartmentIcon sx={{ fontSize: 16 }} />}
              label={user.organization.name}
              sx={{
                display: { xs: "none", md: "flex" },
                maxWidth: 240,
                height: 32,
                bgcolor: "transparent",
                color: "#cbd5e1",
                border: "1px solid rgba(30, 41, 59, 1)",
                "& .MuiChip-icon": { color: "#a78bfa" },
              }}
            />
          )}
          <AccountMenu />
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
